// The only model names the frontend knows.  Every other provider's list comes
// from /api/provider_models (modelListFor), so a new or retiring model is a
// row in that table.
export const MODEL_OPTIONS = {
  // The server refuses any other name under the platform provider
  // (PLATFORM_MODEL_ALIASES in utils/model_utils_helpers.py).
  default: [
    { value: "default-rapid-model", label: "default-rapid-model" },
    { value: "default-model", label: "default-model" },
  ],
};

export const DEFAULT_MODELS = {
  anthropic: "",
  aws: "",
  azure: "",
  google: "",
  local: "",
  openai: "",
  default: "default-model",
};

export const API_PROVIDERS = [
  'anthropic',
  'aws',
  'azure',
  'google',
  'local',
  'openai',
];

// Human-readable display names for provider keys used throughout the UI.
export const PROVIDER_LABELS = {
  anthropic: 'Anthropic',
  aws: 'AWS',
  azure: 'Azure',
  google: 'Google',
  local: 'Local',
  openai: 'OpenAI',
  default: 'Default',
  stackform: 'Stackform (default)',
};

// The credentials of a provider that has no use for an endpoint: the form asks
// for these instead, on every config, beside its API key. Which of them a
// config needs is `isUsableProviderConfig`'s rule.
export const PROVIDER_CREDENTIAL_FIELDS = {
  aws: [
    { name: 'access_key', label: 'Access Key', type: 'password', placeholder: 'Enter Access Key ID' },
    { name: 'secret_key', label: 'Secret Key', type: 'password', placeholder: 'Enter Secret Access Key' },
    { name: 'region', label: 'Region', type: 'text', placeholder: 'Enter AWS Region (e.g., us-east-1, eu-west-1)' }
  ]
};

// Strip the "_N" config-index suffix from a provider key ("anthropic_2"
// → "anthropic"), as utils.model_utils_helpers.provider_base does.
export const providerBase = (key) => {
  if (!key) return key;
  const i = key.lastIndexOf('_');
  return i > 0 && /^\d+$/.test(key.slice(i + 1)) ? key.slice(0, i) : key;
};

// The models a provider's dropdown lists: the platform catalog, then the
// user's custom models (which the API keys by provider family).  `catalog`
// is null until /api/provider_models loads, and only the platform's aliases
// are known meanwhile.  `availableModels` is null until /api/user/api_keys
// loads, and lists none meanwhile.  Shared by Settings and ModelSelect.
export const modelListFor = (
  base,
  { catalog = null, availableModels = {} } = {}
) => {
  const catalogModels = catalog
    ? catalog[base] || []
    : (MODEL_OPTIONS[base] || []).map((o) => o.value);
  const custom = (availableModels?.[base] || []).filter(
    (m) => !catalogModels.includes(m)
  );
  return { catalog: catalogModels, custom, all: [...catalogModels, ...custom] };
};

// An option for a saved choice the list does not have, so the dropdown
// shows it instead of "Select model" while runs still use it.  Once the
// catalog has loaded, missing means the user removed the custom model
// (or the platform retired it), so the label says so.  null when the
// choice is empty or listed.
export const unlistedOption = (models, value, { catalogLoaded = true } = {}) =>
  value && !models.includes(value)
    ? { value, label: catalogLoaded ? `${value} (removed)` : value }
    : null;

// Ordered candidate model values for a provider: the list its model field
// offers (modelListFor: the catalog, then the user's custom models), or a
// local key's device models.
export const getCandidateModels = (
  provider,
  baseProvider,
  { catalog = null, availableModels = {}, localProviderModels = {} } = {}
) => {
  if (baseProvider === 'local') {
    return localProviderModels[provider] || [];
  }
  return modelListFor(baseProvider, { catalog, availableModels }).all;
};

// Default model when a provider is (re)selected: the recommended model
// when it is available in the candidate list, otherwise the first
// candidate ('' when there are none).  This is why selecting a provider
// lands on e.g. "gpt-5.4-mini (recommended)" instead of the first
// catalog entry.
export const pickDefaultModel = (
  provider,
  baseProvider,
  {
    catalog = null,
    availableModels = {},
    localProviderModels = {},
    modelRecommended = {},
  } = {}
) => {
  const candidates = getCandidateModels(provider, baseProvider, {
    catalog,
    availableModels,
    localProviderModels,
  });
  const recommended = modelRecommended[baseProvider];
  if (recommended && candidates.includes(recommended)) return recommended;
  return candidates[0] || '';
};

// Helper functions for "default" provider display labels
export const getDisplayLabel = (provider, modelValue) => {
  if (provider === 'default' && MODEL_OPTIONS.default) {
    const option = MODEL_OPTIONS.default.find(opt => opt.value === modelValue);
    return option ? option.label : modelValue;
  }
  return modelValue;
};

export const getModelValue = (provider, displayLabel) => {
  if (provider === 'default' && MODEL_OPTIONS.default) {
    const option = MODEL_OPTIONS.default.find(opt => opt.label === displayLabel);
    return option ? option.value : displayLabel;
  }
  return displayLabel;
};

